// frontend/src/components/profile/MyGarage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { vehiclesApi } from '../../api/vehicles';
import { FaCar, FaPlus, FaTrash, FaInfoCircle, FaTools, FaIdCard, FaTachometerAlt, FaHistory } from 'react-icons/fa';

function MyGarage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    plate_number: '',
    vin: '',
    engine_number: '',
    current_mileage: '',
    last_service_date: '',
    previous_faults: ''
  });

  useEffect(() => {
    fetchGarage();
  }, []);

  const fetchGarage = async () => {
    try {
      const data = await vehiclesApi.getGarage();
      setVehicles(data);
    } catch (err) {
      console.error("Error fetching garage:", err);
      setError("Failed to load your garage.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...formData,
        year: parseInt(formData.year),
        current_mileage: formData.current_mileage ? parseInt(formData.current_mileage) : 0,
        // Send null if empty string
        plate_number: formData.plate_number || null,
        vin: formData.vin || null,
        engine_number: formData.engine_number || null,
        last_service_date: formData.last_service_date || null,
        previous_faults: formData.previous_faults || null
      };

      await vehiclesApi.addToGarage(payload);
      
      setSuccess("Vehicle added to garage successfully!");
      setFormData({
        make: '', model: '', year: new Date().getFullYear(), color: '',
        plate_number: '', vin: '', engine_number: '', current_mileage: '',
        last_service_date: '', previous_faults: ''
      });
      setShowAddForm(false);
      fetchGarage(); 
    } catch (err) {
      console.error("Add vehicle error:", err);
      setError(err.message || "Failed to add vehicle.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to remove this vehicle from your garage?")) {
      try {
        await vehiclesApi.removeFromGarage(id);
        setVehicles(vehicles.filter(v => v.id !== id));
      } catch (err) {
        alert("Failed to remove vehicle.");
      }
    }
  };

  if (loading) return <div className="text-center py-10">Loading your garage...</div>;

  return (
    <div>
      {/* UPDATED HEADER: Stack on mobile, Row on desktop */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <FaCar className="text-primary" /> My Garage
        </h2>
        <button 
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-primary hover:bg-primary-dark text-white text-sm font-bold py-2 px-4 rounded flex items-center justify-center gap-2 transition duration-300 w-full sm:w-auto"
        >
          {showAddForm ? 'Cancel' : <><FaPlus /> Add Vehicle</>}
        </button>
      </div>

      {/* --- TOOLKIT / INFO SECTION --- */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 rounded-r-lg shadow-sm">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <FaInfoCircle className="text-blue-500 text-xl mt-1" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-bold text-blue-800">Why add detailed info?</h3>
            <div className="mt-2 text-sm text-blue-700 space-y-1">
              <p>Providing details like <strong>Mileage</strong> and <strong>VIN</strong> helps our AI Mechanic give you accurate maintenance advice and helps our technicians prepare for your service visits.</p>
            </div>
          </div>
        </div>
      </div>

      {/* --- ADD VEHICLE FORM --- */}
      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8 shadow-sm animate-fadeInUp">
          <h3 className="font-bold text-gray-800 mb-4">Add New Vehicle</h3>
          <form onSubmit={handleAddVehicle}>
            
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Make *</label>
                <input type="text" name="make" required value={formData.make} onChange={handleInputChange} placeholder="e.g. Toyota" className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Model *</label>
                <input type="text" name="model" required value={formData.model} onChange={handleInputChange} placeholder="e.g. Corolla" className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Year *</label>
                <input type="number" name="year" required value={formData.year} onChange={handleInputChange} className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Color</label>
                <input type="text" name="color" value={formData.color} onChange={handleInputChange} placeholder="e.g. Silver" className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary" />
              </div>
            </div>

            {/* Technical Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Plate Number</label>
                <input type="text" name="plate_number" value={formData.plate_number} onChange={handleInputChange} placeholder="ABC-123DE" className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">VIN / Chassis No.</label>
                <input type="text" name="vin" value={formData.vin} onChange={handleInputChange} placeholder="17-digit VIN" className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Engine Number</label>
                <input type="text" name="engine_number" value={formData.engine_number} onChange={handleInputChange} placeholder="Engine No." className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary" />
              </div>
            </div>

            {/* Service Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Current Mileage (km)</label>
                <input type="number" name="current_mileage" value={formData.current_mileage} onChange={handleInputChange} placeholder="e.g. 120000" className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Last Service Date</label>
                <input type="date" name="last_service_date" value={formData.last_service_date} onChange={handleInputChange} className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary" />
              </div>
            </div>

            <div className="mb-4">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Previous Faults / History</label>
                <textarea name="previous_faults" value={formData.previous_faults} onChange={handleInputChange} rows="3" placeholder="e.g. Replaced alternator in 2023, Brake pads worn..." className="w-full px-3 py-2 border rounded focus:outline-none focus:border-primary"></textarea>
            </div>
            
            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
            
            <button 
              type="submit" 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded transition duration-300"
            >
              {submitting ? 'Adding...' : 'Save Vehicle'}
            </button>
          </form>
        </div>
      )}

      {success && (
        <div className="bg-green-100 text-green-700 p-3 rounded mb-6 flex items-center gap-2">
          <FaTools /> {success}
        </div>
      )}

      {/* --- VEHICLE LIST --- */}
      {vehicles.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <FaCar className="mx-auto text-gray-300 text-4xl mb-3" />
          <p className="text-gray-500 mb-4">Your garage is empty.</p>
          <button onClick={() => setShowAddForm(true)} className="text-primary hover:underline font-medium">
            Add your first car
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vehicles.map((vehicle) => (
            <div key={vehicle.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow relative group">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-lg text-gray-800">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h3>
                  <div className="text-sm text-gray-500 mt-2 space-y-1">
                    {vehicle.plate_number && <p className="flex items-center gap-2"><FaIdCard className="text-gray-400" /> {vehicle.plate_number}</p>}
                    {vehicle.current_mileage > 0 && <p className="flex items-center gap-2"><FaTachometerAlt className="text-gray-400" /> {vehicle.current_mileage.toLocaleString()} km</p>}
                    {vehicle.last_service_date && <p className="flex items-center gap-2"><FaHistory className="text-gray-400" /> Last Service: {vehicle.last_service_date}</p>}
                  </div>
                </div>
                <div className="bg-gray-100 p-2 rounded-full text-gray-400">
                  <FaCar size={24} />
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                <Link 
                  to={`/services/schedule?vehicle=${vehicle.id}`}
                  className="text-sm font-bold text-primary hover:underline flex items-center gap-1"
                >
                  <FaTools /> Book Service
                </Link>
                <button 
                  onClick={() => handleDelete(vehicle.id)}
                  className="text-red-400 hover:text-red-600 transition-colors p-1"
                  title="Remove from Garage"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyGarage;